import React from 'react';
import { startOfWeek, endOfWeek, eachDayOfInterval, format } from 'date-fns';
import clsx from 'clsx';

import { ResourceBookingSchema } from "../data/types";
import { getBookingStatusColor } from "../utils/helpers";

type WeekViewProps = {
  currentDate: number | Date
  bookings: ResourceBookingSchema[]
  onBookingClick: (booking: ResourceBookingSchema) => void
}

const WeekView: React.FC<WeekViewProps> = ({bookings, currentDate, onBookingClick}) => {
  const [timezone, setTimezone] = React.useState('EST');
  const timezones = ['GMT + 1', 'GMT', 'GMT - 1', 'EST', 'PST']; // Example timezones

  const firstDayOfWeek = startOfWeek(currentDate);
  const lastDayOfWeek = endOfWeek(currentDate);

  const daysInWeek = eachDayOfInterval({ start: firstDayOfWeek, end: lastDayOfWeek });

  // Only show 1 AM to 12 AM
  const timeSlots = Array.from({ length: 24 }, (_, i) => i + 1); // 1 to 24 (1 AM to 12 AM)
  const selectedDay = new Date(currentDate).getDate();

  return (
    <div className="overflow-x-auto border-[1px] border-[#E9E9E9] rounded-lg mx-4">
      {/* Day headers and time zone selector */}
      <div className="flex" style={{ minWidth: 1100 }}>
        {/* Time zone selector in the first column */}
        <div className="w-28 bg-white flex flex-col items-center justify-center pr-2 pt-2">
          <label className="block text-xs text-gray-400 mb-1 text-center">Time zone</label>
          <select
            className="text-xs text-center text-gray-500 border-0 rounded-md bg-transparent focus:outline-none"
            value={timezone}
            disabled={true}
            onChange={e => setTimezone(e.target.value)}
          >
            {timezones.map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
        {/* Day headers */}
        {daysInWeek.map((day) => (
          <div
            key={day.toISOString()}
            className={clsx(
              'flex-1 sticky top-0 z-20 flex flex-col items-center border-b border-gray-200 py-4',
              day.getDate() === selectedDay ? 'bg-primary-200 text-white rounded-t' : 'bg-white text-gray-700'
            )}
            style={{ background: day.getDate() === selectedDay ? undefined : '#fff' }}
          >
            <span className="text-xs font-medium">{format(day, 'E')}</span>
            <span className="text-base font-semibold">{format(day, 'd')}</span>
          </div>
        ))}
      </div>
      {/* Scrollable grid */}
      <div className="flex" style={{ minWidth: 1100, maxHeight: 700, overflowY: 'auto' }}>
        {/* Time column */}
        <div className="w-28 bg-white sticky left-0 z-30">
          <div className="relative" style={{ height: `${timeSlots.length * 64}px` }}>
            {timeSlots.map((hour, i) => (
              <div
                key={hour}
                className="h-16 flex items-center justify-end pr-2 text-xs text-gray-400 border-b border-gray-200 border-r"
                style={{ background: i % 2 === 0 ? '#fafbfc' : '#fff' }}
              >
                {(hour < 24) ? hour <= 12 ? `${hour} AM` : `${hour - 12} PM` : `${hour - 12} AM`}
              </div>
            ))}
          </div>
        </div>
        {/* Day columns */}
        {daysInWeek.map((day, dayIdx) => (
          <div
            key={day.toISOString() + '-col'}
            className={clsx(
              'flex-1 relative border-r border-gray-200',
              day.getDate() === selectedDay ? 'bg-primary-200/10' : dayIdx % 2 === 0 ? 'bg-[#fafbfc]' : 'bg-white'
            )}
            style={{ minHeight: `${timeSlots.length * 64}px` }}
          >
            <div className="relative h-full">
              {bookings && bookings.length > 0 && bookings.map((booking, index) => {
                const eventStartDate = new Date(booking?.start_time || "");
                const eventEndDate = new Date(booking?.end_time || "");
                if (
                  isNaN(eventStartDate.getTime()) ||
                  isNaN(eventEndDate.getTime())
                ) return null;
                if (
                  eventStartDate.getDate() === day.getDate() &&
                  eventStartDate.getMonth() === day.getMonth() &&
                  eventStartDate.getFullYear() === day.getFullYear()
                ) {
                  if (eventStartDate.getHours() < 1 || eventStartDate.getHours() > 21) return null;
                  const slotHeight = 64;
                  const top = ((eventStartDate.getHours() - 1) * slotHeight) + (eventStartDate.getMinutes() * slotHeight / 60);
                  const duration = (eventEndDate.getTime() - eventStartDate.getTime()) / (1000 * 60 * 60);
                  const height = Math.max(duration * slotHeight, 32);
                  const color = getBookingStatusColor(booking.status || "");
                  return (
                    <div
                      key={index}
                      className="absolute left-1 right-1 rounded-lg shadow-md px-2 py-1 text-xs font-semibold flex flex-col justify-center cursor-pointer border border-white transition hover:shadow-lg hover:scale-[1.02]"
                      style={{
                        top: `${top}px`,
                        height: `${height}px`,
                        background: color.bgColor,
                        color: color.primaryTextColor,
                        zIndex: 10,
                      }}
                      title={booking.summary || `${booking.resource_name} for ${booking.user_full_name || booking.username}`}
                      onClick={() => onBookingClick(booking)}
                    >
                      <span className="truncate">
                        {`${format(eventStartDate, 'h:mm a')} - ${format(eventEndDate, 'h:mm a')}`}
                      </span>
                      <span className="font-normal text-[11px] opacity-80 truncate">{booking.summary || `${booking.resource_name} for ${booking.user_full_name || booking.username}`}</span>
                    </div>
                  );
                }
                return null;
              })}
              {/* Grid lines for time slots */}
              {timeSlots.map((_, i) => (
                <div
                  key={i}
                  className="absolute left-0 right-0 border-b border-dashed border-gray-200"
                  style={{ top: `${i * 64}px` }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeekView;
